import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SESSION_TEMPLATE_APPLIED_SESSION_KEY,
  SESSION_TEMPLATE_STORAGE_KEY,
} from './Landingpage.constants';
import type { SessionTemplateChoice } from './Landingpage.types';

export const useLandingPageSessionTemplate = ({
  userId,
  userName,
  userDisplayName,
  handleChatClick,
}: {
  userId?: string;
  userName?: string | null;
  userDisplayName?: string | null;
  handleChatClick: (question: string) => Promise<void>;
}) => {
  const sessionTemplateStorageKey = useMemo(() => {
    const userKey = userId || userName || userDisplayName;
    return `${SESSION_TEMPLATE_STORAGE_KEY}:${userKey || 'anonymous'}`;
  }, [userId, userName, userDisplayName]);

  const sessionTemplateAppliedSessionKey = useMemo(() => {
    const userKey = userId || userName || userDisplayName;
    return `${SESSION_TEMPLATE_APPLIED_SESSION_KEY}:${userKey || 'anonymous'}`;
  }, [userId, userName, userDisplayName]);

  const [showSessionTemplateModal, setShowSessionTemplateModal] =
    useState(false);
  const [isApplyingSessionTemplate, setIsApplyingSessionTemplate] =
    useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const saved = window.localStorage.getItem(sessionTemplateStorageKey);
      setShowSessionTemplateModal(
        saved !== 'ask-session-template' &&
          saved !== 'workspace-session-template',
      );
    } catch {
      setShowSessionTemplateModal(true);
    }
  }, [sessionTemplateStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined' || showSessionTemplateModal) return;

    let savedChoice: SessionTemplateChoice | null = null;
    try {
      const saved = window.localStorage.getItem(sessionTemplateStorageKey);
      if (
        saved === 'ask-session-template' ||
        saved === 'workspace-session-template'
      ) {
        savedChoice = saved;
      }
    } catch {
      return;
    }

    if (!savedChoice) return;

    try {
      const alreadyApplied =
        window.sessionStorage.getItem(sessionTemplateAppliedSessionKey) === '1';
      if (alreadyApplied) return;
      window.sessionStorage.setItem(sessionTemplateAppliedSessionKey, '1');
    } catch {
      return;
    }

    if (savedChoice === 'ask-session-template') {
      setIsApplyingSessionTemplate(true);
      void handleChatClick('').finally(() => {
        setIsApplyingSessionTemplate(false);
      });
    }
  }, [
    handleChatClick,
    sessionTemplateAppliedSessionKey,
    sessionTemplateStorageKey,
    showSessionTemplateModal,
  ]);

  const persistSessionTemplateChoice = useCallback(
    (choice: SessionTemplateChoice) => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(sessionTemplateStorageKey, choice);
      } catch {
        // ignore localStorage errors
      }
    },
    [sessionTemplateStorageKey],
  );

  const markSessionTemplateApplied = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(sessionTemplateAppliedSessionKey, '1');
    } catch {
      // ignore sessionStorage errors
    }
  }, [sessionTemplateAppliedSessionKey]);

  const handleAskSessionTemplateChoice = useCallback(async () => {
    persistSessionTemplateChoice('ask-session-template');
    markSessionTemplateApplied();
    setShowSessionTemplateModal(false);
    setIsApplyingSessionTemplate(true);
    try {
      await handleChatClick('');
    } finally {
      setIsApplyingSessionTemplate(false);
    }
  }, [
    handleChatClick,
    persistSessionTemplateChoice,
    markSessionTemplateApplied,
  ]);

  const handleWorkspaceSessionTemplateChoice = useCallback(() => {
    persistSessionTemplateChoice('workspace-session-template');
    markSessionTemplateApplied();
    setShowSessionTemplateModal(false);
  }, [persistSessionTemplateChoice, markSessionTemplateApplied]);

  return {
    showSessionTemplateModal,
    isApplyingSessionTemplate,
    handleAskSessionTemplateChoice,
    handleWorkspaceSessionTemplateChoice,
  };
};
